import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Input, Select, Tooltip } from 'antd'
import { Formik } from 'formik'
import { useHistory, useParams } from 'react-router-dom'
import React, { useEffect, useState } from 'react'

import { CursoModel } from '../../models/CursoModel'
import { ModuloModel } from '../../models/ModuloModel'
import { ConteudoPaginaModel } from '../../models/ConteudoPaginaModel'
import api from '../../api'
import { ModulosView } from '../Modulos/ModulosView'
import { AlunosView } from '../Alunos'
import { Show } from '../../components/Basic'
import { DownloadsCursosView } from '../Downloads/DownloadsCurso'
import { SelectField } from '../../components/FormikInput'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { SiteMetadataEditor } from '../../components/SiteMetadataEditor'
import { hasPerm, useAuthState } from '../../stores/authStore'
import { PERMS } from '../../permissions/perms'

interface CursosEditarParams {
  cursoId: string
}

export function CursosEditar() {
  const history = useHistory()
  const { cursoId } = useParams<CursosEditarParams>()
  const authState = useAuthState()

  const [curso, setCurso] = useState({ nome: '' } as any)

  useEffect(() => {
    let isMounted = true

    if (!cursoId) {
      setCurso({ nome: '' } as any)
      return () => {
        isMounted = false
      }
    }

    api.Cursos.get({ includeSiteMetadata: true } as any, +cursoId)
      .then((data) => {
        if (isMounted) {
          setCurso(data.rows[0] ?? ({ nome: '' } as any))
        }
      })
      .catch((error) => console.log({ error }))

    return () => {
      isMounted = false
    }
  }, [cursoId])

  const backToCursos = () => history.push('/cursos/')
  const isEditing = !!curso.id
  const canSeeSiteMetadataEditor = hasPerm(authState, PERMS.SITE_METADADOS_EDITAR)

  return (
    <Layout>
      <PageHeader>
        <Title>{isEditing ? `${curso.nomeReduzido}` : 'Novo Curso'}</Title>
        <Button onClick={backToCursos} icon={<ArrowLeftOutlined />}>
          Voltar
        </Button>
      </PageHeader>

      <ContentStack>
        <DadosCurso isEditing={isEditing} curso={curso} />
        <Show condition={isEditing}>
          <CourseSideBySideGrid>
            <ModulosView cursoId={+cursoId} />
            {/* <AlunosDoCurso /> */}
            <DownloadsCursosView cursoId={+cursoId} />
          </CourseSideBySideGrid>
          <Show condition={canSeeSiteMetadataEditor}>
            <SiteMetadataDoCurso />
          </Show>
        </Show>
      </ContentStack>
    </Layout>
  )
}

interface BlocoConteudoProps {
  children
}
const BlocoConteudo: React.FC<BlocoConteudoProps> = ({ children }) => (
  <SectionCard>{children || null}</SectionCard>
)

const AlunosDoCurso: React.FC = () => {
  const { cursoId } = useParams<CursosEditarParams>()
  return (
    <BlocoConteudo>
      <SectionTitle>Alunos do curso</SectionTitle>
      <AlunosView cursoId={+cursoId} />
    </BlocoConteudo>
  )
}

interface DadosCursoProps {
  isEditing: boolean
  curso: Partial<CursoModel>
}

type PaginasPorModulo = Record<number, ConteudoPaginaModel[]>

const ordenarModulos = (modulos: ModuloModel[]): ModuloModel[] =>
  [...modulos].sort((a, b) => {
    const ordenacaoA = Number(a.ordenacao) || 0
    const ordenacaoB = Number(b.ordenacao) || 0
    if (ordenacaoA !== ordenacaoB) return ordenacaoA - ordenacaoB
    return Number(a.id) - Number(b.id)
  })

const ordenarPaginasModulo = (paginas: ConteudoPaginaModel[]): ConteudoPaginaModel[] =>
  [...paginas].sort((a, b) => {
    if (a.numero !== b.numero) return a.numero - b.numero
    return `${a.id}`.localeCompare(`${b.id}`)
  })

function getPaginaGlobalBySelecao(
  moduloId: number | null,
  paginaId: string | null,
  modulos: ModuloModel[],
  paginasPorModulo: PaginasPorModulo
): number | null {
  if (!moduloId || !paginaId) return null

  let indexGlobal = 0
  for (const modulo of ordenarModulos(modulos)) {
    const paginasModulo = ordenarPaginasModulo(paginasPorModulo[modulo.id] ?? [])
    for (const pagina of paginasModulo) {
      indexGlobal += 1
      if (modulo.id === moduloId && `${pagina.id}` === `${paginaId}`) {
        return indexGlobal
      }
    }
  }

  return null
}

function getSelecaoByPaginaGlobal(
  paginaGlobal: number | null | undefined,
  modulos: ModuloModel[],
  paginasPorModulo: PaginasPorModulo
): { moduloId: number; paginaId: string } | null {
  const paginaGlobalNumerica = Number(paginaGlobal)
  if (!Number.isInteger(paginaGlobalNumerica) || paginaGlobalNumerica <= 0) return null

  let indexGlobal = 0
  for (const modulo of ordenarModulos(modulos)) {
    const paginasModulo = ordenarPaginasModulo(paginasPorModulo[modulo.id] ?? [])
    for (const pagina of paginasModulo) {
      indexGlobal += 1
      if (indexGlobal === paginaGlobalNumerica) {
        return { moduloId: modulo.id, paginaId: `${pagina.id}` }
      }
    }
  }

  return null
}

const DadosCurso: React.FC<DadosCursoProps> = ({ isEditing, curso }: { isEditing?; curso }) => {
  const history = useHistory()
  const cursoId = Number(curso?.id) || null
  const [loadingPaginasLimite, setLoadingPaginasLimite] = useState(false)
  const [modulosCurso, setModulosCurso] = useState<ModuloModel[]>([])
  const [paginasPorModulo, setPaginasPorModulo] = useState<PaginasPorModulo>({})
  const [moduloBloqueioSelecionadoId, setModuloBloqueioSelecionadoId] = useState<number | null>(
    null
  )
  const [paginaBloqueioSelecionadaId, setPaginaBloqueioSelecionadaId] = useState<string | null>(
    null
  )

  const tipoCurso = [
    {
      key: 'CREDENCIADO_DETRAN',
      value: 'CREDENCIADO_DETRAN',
      text: 'Credenenciado Detran',
    },
    { key: 'LIVRE', value: 'LIVRE', text: 'Livre' },
  ]

  const categorias = [
    {
      key: 'Curso de Especialização',
      value: 'Curso de Especialização',
      text: 'Curso de Especialização',
    },
    { key: 'Curso de Atualização', value: 'Curso de Atualização', text: 'Curso de Atualização' },
    { key: 'Curso de Reciclagem', value: 'Curso de Reciclagem', text: 'Curso de Reciclagem' },
    { key: 'Curso Livre', value: 'Curso Livre', text: 'Curso Livre' },
  ]

  useEffect(() => {
    let isMounted = true

    if (!cursoId) {
      setModulosCurso([])
      setPaginasPorModulo({})
      setModuloBloqueioSelecionadoId(null)
      setPaginaBloqueioSelecionadaId(null)
      return () => {
        isMounted = false
      }
    }

    ;(async () => {
      try {
        setLoadingPaginasLimite(true)
        const modulosResponse = await api.Modulos.get({ limit: 1000, offset: 0 }, cursoId)
        const modulos = ordenarModulos(modulosResponse?.rows ?? [])

        const paginasEntries = await Promise.all(
          modulos.map(async (modulo) => {
            const paginas = await api.Paginas.getByModulo(modulo.id)
            const paginasPublicadas = (paginas ?? []).filter(
              (pagina) => pagina.status === 'PUBLICADO'
            )
            return [modulo.id, ordenarPaginasModulo(paginasPublicadas)] as const
          })
        )

        if (!isMounted) return

        const novoMapaPaginas: PaginasPorModulo = {}
        paginasEntries.forEach(([moduloId, paginas]) => {
          novoMapaPaginas[moduloId] = paginas
        })

        setModulosCurso(modulos)
        setPaginasPorModulo(novoMapaPaginas)
      } catch (error) {
        if (isMounted) {
          toast.error('Não foi possível carregar os módulos e páginas para configurar o bloqueio.')
        }
      } finally {
        if (isMounted) {
          setLoadingPaginasLimite(false)
        }
      }
    })()

    return () => {
      isMounted = false
    }
  }, [cursoId])

  useEffect(() => {
    const selecao = getSelecaoByPaginaGlobal(
      curso?.paginaLimiteGratuita,
      modulosCurso,
      paginasPorModulo
    )

    if (!selecao) {
      setModuloBloqueioSelecionadoId(null)
      setPaginaBloqueioSelecionadaId(null)
      return
    }

    setModuloBloqueioSelecionadoId(selecao.moduloId)
    setPaginaBloqueioSelecionadaId(selecao.paginaId)
  }, [curso?.paginaLimiteGratuita, modulosCurso, paginasPorModulo])

  const modulosOptions = ordenarModulos(modulosCurso).map((modulo) => ({
    value: modulo.id,
    label: modulo.nome ? `${modulo.nome}` : `Módulo ${modulo.id}`,
  }))

  const paginasModuloSelecionado = moduloBloqueioSelecionadoId
    ? (paginasPorModulo[moduloBloqueioSelecionadoId] ?? [])
    : []

  const paginasOptions = paginasModuloSelecionado.map((pagina) => ({
    value: `${pagina.id}`,
    label: `Página ${pagina.numero}`,
  }))

  return (
    <BlocoConteudo>
      <SectionTitle>Dados do curso</SectionTitle>
      <Formik
        enableReinitialize
        initialValues={curso}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const campos = [
              'nome',
              'nomeReduzido',
              'diasParaEmissaoCertificado',
              'validadeCurso',
              'tipoCurso',
              'categorias',
              'cargaHoraria',
            ]
            let campoVazio = false
            campos.forEach((campo) => {
              if (!values[campo]) {
                campoVazio = true
              }
            })

            if (campoVazio) {
              toast.error('Preencha todos os campos')
              return
            }

            const savedCurso = await api.Cursos.post(values)
            toast.success('Curso salvo com sucesso')

            if (!isEditing && savedCurso?.id) {
              history.replace(`/cursos/${savedCurso.id}/editar`)
            }
          } catch (e) {
            console.log(e)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
          <StyledForm onSubmit={handleSubmit}>
            <FormGrid>
              <FieldGroup className="full-width">
                <FieldLabel>Id</FieldLabel>
                <Input disabled name="id" onChange={handleChange} value={values.id} />
              </FieldGroup>

              <FieldGroup className="full-width">
                <FieldLabel>Nome</FieldLabel>
                <Input
                  {...(!isEditing ? { autoFocus: true } : {})}
                  name="nome"
                  onChange={handleChange}
                  value={values.nome}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>
                  <Tooltip
                    title={
                      <div>
                        Nome reduzido do curso. Por exemplo:
                        <div>Passageiros</div>
                        <div>At. Passageiros</div>
                        <div>Escolar</div>
                        <div>At. Escolar</div>
                      </div>
                    }
                  >
                    Nome Reduzido
                  </Tooltip>
                </FieldLabel>
                <Input name="nomeReduzido" onChange={handleChange} value={values.nomeReduzido} />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Dias para emissão do certificado</FieldLabel>
                <Input
                  name="diasParaEmissaoCertificado"
                  type="number"
                  min={1}
                  onChange={handleChange}
                  value={values.diasParaEmissaoCertificado}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Anos de Validade do Curso</FieldLabel>
                <Input
                  name="validadeCurso"
                  type="number"
                  min={1}
                  onChange={handleChange}
                  value={values.validadeCurso}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Carga Horária</FieldLabel>
                <Input
                  name="cargaHoraria"
                  type="number"
                  min={1}
                  onChange={handleChange}
                  value={values.cargaHoraria}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>
                  <Tooltip title={<div>Tipo de curso livre não é registrado no DETRAN</div>}>
                    Tipo de Curso
                  </Tooltip>
                </FieldLabel>
                <SelectField
                  onChange={(_, e) => {
                    const { name, value } = e
                    setFieldValue(name, value)
                  }}
                  name="tipoCurso"
                  options={tipoCurso}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Categorias</FieldLabel>
                <SelectField
                  onChange={(_, e) => {
                    const { name, value } = e
                    setFieldValue(name, value)
                  }}
                  name="categorias"
                  options={categorias}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Módulo para bloqueio gratuito</FieldLabel>
                <Select
                  allowClear
                  placeholder={
                    cursoId
                      ? 'Selecione o módulo'
                      : 'Salve o curso para configurar módulo e página de bloqueio'
                  }
                  options={modulosOptions}
                  value={moduloBloqueioSelecionadoId ?? undefined}
                  loading={loadingPaginasLimite}
                  disabled={!cursoId || loadingPaginasLimite || modulosOptions.length === 0}
                  onChange={(value?: number) => {
                    setModuloBloqueioSelecionadoId(value ?? null)
                    setPaginaBloqueioSelecionadaId(null)
                    setFieldValue('paginaLimiteGratuita', null)
                  }}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Página do módulo para bloqueio gratuito</FieldLabel>
                <Select
                  allowClear
                  placeholder={
                    moduloBloqueioSelecionadoId
                      ? 'Selecione a página'
                      : 'Selecione primeiro o módulo'
                  }
                  options={paginasOptions}
                  value={paginaBloqueioSelecionadaId ?? undefined}
                  loading={loadingPaginasLimite}
                  disabled={
                    !cursoId ||
                    !moduloBloqueioSelecionadoId ||
                    loadingPaginasLimite ||
                    paginasOptions.length === 0
                  }
                  onChange={(value?: string) => {
                    const paginaId = value ?? null
                    setPaginaBloqueioSelecionadaId(paginaId)

                    const paginaGlobal = getPaginaGlobalBySelecao(
                      moduloBloqueioSelecionadoId,
                      paginaId,
                      modulosCurso,
                      paginasPorModulo
                    )
                    setFieldValue('paginaLimiteGratuita', paginaGlobal)
                  }}
                />
              </FieldGroup>
            </FormGrid>

            <FormActions>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={values.nome === '' || isSubmitting}
              >
                Salvar
              </Button>
            </FormActions>
          </StyledForm>
        )}
      </Formik>
    </BlocoConteudo>
  )
}

const SiteMetadataDoCurso: React.FC = () => {
  const { cursoId } = useParams<CursosEditarParams>()
  const [curso, setCurso] = useState<CursoModel | null>(null)

  React.useEffect(() => {
    if (cursoId) {
      api.Cursos.get({ includeSiteMetadata: true } as any, +cursoId)
        .then((data) => setCurso(data.rows[0]))
        .catch((error) => console.log({ error }))
    }
  }, [cursoId])

  return (
    <SiteMetadataEditor
      cursoId={+cursoId}
      initialData={curso?.siteMetadata}
      onUpdate={(data) => {
        if (curso) {
          setCurso({ ...curso, siteMetadata: data })
        }
      }}
    />
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 24px;
`

const CourseSideBySideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  padding: 20px;
`

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #494d50;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  .full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;

    .full-width {
      grid-column: auto;
    }
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .ant-input {
    min-height: 36px;
    border-radius: 8px;
    width: 100%;
  }

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    min-height: 36px !important;
    border-radius: 10px !important;
    display: flex;
    align-items: center;
    padding: 0 11px !important;
  }
`

const FieldLabel = styled.label`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
`

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`
